import { type CSSProperties, useMemo } from 'react';
import { usePopper } from 'react-popper';
import { getDropdownPopperOptions } from './DropdownList.popper';

type UseDropdownFloatingPopperParams = {
  isFloating: boolean;
  triggerElement: HTMLElement | null;
  panelElement: HTMLElement | null;
  position: 'up' | 'down';
};

type UseDropdownFloatingPopperResult = {
  floatingStyle: CSSProperties | undefined;
  floatingAttributes: Record<string, string> | undefined;
  floatingPosition: 'up' | 'down' | undefined;
  floatingMaxHeight: number | undefined;
};

/**
 * Owns the Popper wiring for {@link DropdownList}'s floating (portalled) mode: placement, viewport
 * clamping, and the custom `dropdownMaxHeight` modifier. Returns floating-only values (all
 * `undefined` when not floating) so the inline positioning path stays untouched.
 */
export function useDropdownFloatingPopper({
  isFloating,
  triggerElement,
  panelElement,
  position,
}: UseDropdownFloatingPopperParams): UseDropdownFloatingPopperResult {
  const popperOptions = useMemo(
    () => getDropdownPopperOptions(position === 'up' ? 'top-start' : 'bottom-start'),
    [position],
  );

  const {
    styles: popperStyles,
    attributes: popperAttributes,
    state: popperState,
  } = usePopper(isFloating ? triggerElement : null, isFloating ? panelElement : null, popperOptions);

  if (!isFloating) {
    return {
      floatingStyle: undefined,
      floatingAttributes: undefined,
      floatingPosition: undefined,
      floatingMaxHeight: undefined,
    };
  }

  const rawFloatingHeight = popperState?.modifiersData?.dropdownMaxHeight?.height;
  const floatingMaxHeight = typeof rawFloatingHeight === 'number' ? rawFloatingHeight : undefined;

  const placement = popperState?.placement;
  let floatingPosition: 'up' | 'down' | undefined;
  if (placement) {
    floatingPosition = placement.startsWith('top') ? 'up' : 'down';
  }

  return {
    floatingStyle: popperStyles.popper,
    floatingAttributes: popperAttributes.popper,
    floatingPosition,
    floatingMaxHeight,
  };
}
